import { StaticPageLayout } from '@/components/StaticPageLayout';
import { Users, Calendar, Award, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from '@inertiajs/react';

export default function AboutPage() {
  const timeline = [
    {
      year: '2019',
      event:
        'Founded by medical students who experienced firsthand the inequities in the application process',
    },
    {
      year: '2020',
      event:
        'Expanded to include graduate school mentorship beyond just medical programs',
    },
    {
      year: '2021',
      event:
        'Achieved 501(c)(3) nonprofit status and formalized our organizational structure',
    },
    { year: '2022', event: 'Reached 1,000+ mentees across all 50 states' },
    {
      year: 'Today',
      event:
        'Operating with 100+ active volunteer mentors supporting students nationwide',
    },
  ];

  const principles = [
    {
      number: '01',
      title: 'No student left behind',
      description:
        'We actively seek out first-generation, low-income, and underrepresented students who often lack access to traditional mentorship networks.',
    },
    {
      number: '02',
      title: 'Quality over quantity',
      description:
        'Each mentee receives personalized, one-on-one guidance throughout their entire application journey, not generic advice.',
    },
    {
      number: '03',
      title: 'Pay it forward',
      description:
        'Many of our mentors are former mentees who return to help others follow in their footsteps.',
    },
  ];

  return (
    <StaticPageLayout>
      {/* Hero Section */}
      <section className="bg-white overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="py-24 md:py-32">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <p className="text-sm uppercase tracking-widest text-primary mb-4">
                  Our Story
                </p>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-6">
                  Started by students
                  <br />
                  <span className="italic">who've been there,</span>
                  <br />
                  <span className="font-semibold">
                    for students going there
                  </span>
                </h1>
                <p className="text-xl text-gray-700 leading-relaxed max-w-2xl mb-4">
                  Project SHORT was created in 2019 with the goal of combatting
                  socioeconomic and systemic inequalities in the medical and
                  graduate school admissions processes.
                </p>
                <p className="text-xl text-gray-700 leading-relaxed max-w-2xl">
                  Comprised of volunteer professional health and graduate
                  students, residents, faculty, and post-doctoral fellows,
                  <span className="text-primary"> Project SHORT</span> (Students
                  for Higher Education Opportunities and Representation in
                  Training) is the first 501(c)(3) nonprofit to offer pro bono
                  mentoring for both medical and graduate school admissions.
                </p>
              </div>
              <div className="lg:col-span-5 relative">
                {/* Creative visualization: Origami birds taking flight */}
                <div className="relative h-[400px] flex items-center justify-center overflow-hidden">
                  {/* Wind currents */}
                  <svg className="absolute inset-0 w-full h-full opacity-10">
                    <path
                      d="M -50 100 Q 100 150 250 100 T 450 150"
                      stroke="rgb(147 51 234)"
                      strokeWidth="1"
                      fill="none"
                      className="animate-draw"
                    />
                    <path
                      d="M -50 200 Q 150 250 300 200 T 500 250"
                      stroke="rgb(147 51 234)"
                      strokeWidth="1.5"
                      fill="none"
                      className="animate-draw"
                      style={{ animationDelay: '1s' }}
                    />
                    <path
                      d="M -50 300 Q 200 250 350 300 T 550 250"
                      stroke="rgb(147 51 234)"
                      strokeWidth="1"
                      fill="none"
                      className="animate-draw"
                      style={{ animationDelay: '2s' }}
                    />
                  </svg>

                  {/* Large mentor bird leading the way */}
                  <div className="absolute left-[60%] top-[20%] -translate-x-1/2 -translate-y-1/2 transform rotate-[-15deg]">
                    <svg
                      width="80"
                      height="80"
                      viewBox="0 0 100 100"
                      className="animate-float"
                      style={{ animationDuration: '4s' }}
                    >
                      <path
                        d="M 50 20 L 30 40 L 50 35 L 70 40 Z"
                        fill="rgb(147 51 234 / 0.6)"
                        stroke="rgb(147 51 234)"
                        strokeWidth="1"
                      />
                      <path
                        d="M 50 35 L 40 50 L 50 45 L 60 50 Z"
                        fill="rgb(147 51 234 / 0.4)"
                        stroke="rgb(147 51 234)"
                        strokeWidth="1"
                      />
                      <path
                        d="M 50 45 L 45 55 L 50 52 L 55 55 Z"
                        fill="rgb(147 51 234 / 0.3)"
                        stroke="rgb(147 51 234)"
                        strokeWidth="0.5"
                      />
                    </svg>
                  </div>

                  {/* Smaller mentee birds following */}
                  <div className="absolute left-[45%] top-[35%] -translate-x-1/2 -translate-y-1/2 transform rotate-[-20deg]">
                    <svg
                      width="50"
                      height="50"
                      viewBox="0 0 100 100"
                      className="animate-float"
                      style={{
                        animationDuration: '5s',
                        animationDelay: '0.5s',
                      }}
                    >
                      <path
                        d="M 50 30 L 35 45 L 50 40 L 65 45 Z"
                        fill="rgb(59 130 246 / 0.5)"
                        stroke="rgb(59 130 246)"
                        strokeWidth="1"
                      />
                      <path
                        d="M 50 40 L 42 52 L 50 48 L 58 52 Z"
                        fill="rgb(59 130 246 / 0.3)"
                        stroke="rgb(59 130 246)"
                        strokeWidth="0.5"
                      />
                    </svg>
                  </div>

                  <div className="absolute left-[40%] top-[45%] -translate-x-1/2 -translate-y-1/2 transform rotate-[-25deg]">
                    <svg
                      width="45"
                      height="45"
                      viewBox="0 0 100 100"
                      className="animate-float"
                      style={{ animationDuration: '6s', animationDelay: '1s' }}
                    >
                      <path
                        d="M 50 30 L 35 45 L 50 40 L 65 45 Z"
                        fill="rgb(236 72 153 / 0.5)"
                        stroke="rgb(236 72 153)"
                        strokeWidth="1"
                      />
                      <path
                        d="M 50 40 L 42 52 L 50 48 L 58 52 Z"
                        fill="rgb(236 72 153 / 0.3)"
                        stroke="rgb(236 72 153)"
                        strokeWidth="0.5"
                      />
                    </svg>
                  </div>

                  <div className="absolute left-[35%] top-[55%] -translate-x-1/2 -translate-y-1/2 transform rotate-[-18deg]">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 100 100"
                      className="animate-float"
                      style={{
                        animationDuration: '5.5s',
                        animationDelay: '1.5s',
                      }}
                    >
                      <path
                        d="M 50 30 L 35 45 L 50 40 L 65 45 Z"
                        fill="rgb(34 197 94 / 0.5)"
                        stroke="rgb(34 197 94)"
                        strokeWidth="1"
                      />
                      <path
                        d="M 50 40 L 42 52 L 50 48 L 58 52 Z"
                        fill="rgb(34 197 94 / 0.3)"
                        stroke="rgb(34 197 94)"
                        strokeWidth="0.5"
                      />
                    </svg>
                  </div>

                  <div className="absolute left-[30%] top-[65%] -translate-x-1/2 -translate-y-1/2 transform rotate-[-22deg]">
                    <svg
                      width="35"
                      height="35"
                      viewBox="0 0 100 100"
                      className="animate-float"
                      style={{ animationDuration: '7s', animationDelay: '2s' }}
                    >
                      <path
                        d="M 50 30 L 35 45 L 50 40 L 65 45 Z"
                        fill="rgb(251 146 60 / 0.5)"
                        stroke="rgb(251 146 60)"
                        strokeWidth="1"
                      />
                      <path
                        d="M 50 40 L 42 52 L 50 48 L 58 52 Z"
                        fill="rgb(251 146 60 / 0.3)"
                        stroke="rgb(251 146 60)"
                        strokeWidth="0.5"
                      />
                    </svg>
                  </div>

                  {/* Birds still learning to fly */}
                  <div className="absolute left-[15%] bottom-[25%] -translate-x-1/2 translate-y-1/2">
                    <svg
                      width="30"
                      height="30"
                      viewBox="0 0 100 100"
                      className="opacity-50"
                    >
                      <path
                        d="M 50 30 L 35 45 L 50 40 L 65 45 Z"
                        fill="rgb(147 51 234 / 0.2)"
                        stroke="rgb(147 51 234 / 0.5)"
                        strokeWidth="1"
                        strokeDasharray="2,2"
                      />
                    </svg>
                  </div>

                  <div className="absolute left-[25%] bottom-[20%] -translate-x-1/2 translate-y-1/2">
                    <svg
                      width="25"
                      height="25"
                      viewBox="0 0 100 100"
                      className="opacity-40"
                    >
                      <path
                        d="M 50 30 L 35 45 L 50 40 L 65 45 Z"
                        fill="rgb(59 130 246 / 0.2)"
                        stroke="rgb(59 130 246 / 0.5)"
                        strokeWidth="1"
                        strokeDasharray="2,2"
                      />
                    </svg>
                  </div>

                  {/* Destination: open sky */}
                  <div className="absolute top-[10%] right-[15%]">
                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-sky-100/20 to-transparent blur-2xl"></div>
                  </div>

                  {/* Feathers floating */}
                  <div
                    className="absolute top-[40%] right-[30%] animate-float"
                    style={{ animationDuration: '8s' }}
                  >
                    <div className="w-1 h-3 bg-primary/20 rounded-full transform rotate-45"></div>
                  </div>
                  <div
                    className="absolute top-[60%] right-[40%] animate-float"
                    style={{ animationDuration: '9s', animationDelay: '2s' }}
                  >
                    <div className="w-1 h-2.5 bg-blue-400/20 rounded-full transform rotate-[60deg]"></div>
                  </div>
                  <div
                    className="absolute top-[50%] left-[50%] animate-float"
                    style={{ animationDuration: '10s', animationDelay: '1s' }}
                  >
                    <div className="w-1 h-2 bg-pink-400/20 rounded-full transform rotate-[30deg]"></div>
                  </div>

                  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center">
                    <p className="text-sm text-gray-500">
                      Learning to soar, together
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="max-w-4xl">
            <h2 className="text-sm uppercase tracking-widest text-primary mb-6">
              The Problem We Saw
            </h2>
            <p className="text-3xl md:text-4xl font-light leading-relaxed text-gray-800 mb-8">
              Private admissions consultancy is expensive, and perpetuates an
              already-wide socioeconomic gap in STEM and Medicine. This issue
              creates an{' '}
              <span className="text-primary">insurmountable barrier</span> to
              aspiring applicants.
            </p>
            <p className="text-lg text-gray-600">
              By offering free and high-quality mentorship, Project SHORT aims
              to help diversify the pool of applicants applying to graduate and
              medical schools.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      {/* <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="mb-16">
            <h2 className="text-sm uppercase tracking-widest text-primary mb-4">
              Our Journey
            </h2>
            <p className="text-4xl md:text-5xl font-light text-gray-900">
              From idea to impact
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-[1px] bg-primary/20"></div>

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div key={index} className="relative flex items-start">
                  <div className="absolute left-8 w-4 h-4 bg-white border-2 border-primary rounded-full -translate-x-1/2"></div>
                  <div className="ml-20">
                    <div className="text-2xl font-light text-primary mb-2">
                      {item.year}
                    </div>
                    <p className="text-gray-600 max-w-2xl">{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section> */}

      {/* Principles Section */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="mb-16">
            <h2 className="text-sm uppercase tracking-widest text-primary mb-4">
              Guiding Principles
            </h2>
            <p className="text-4xl md:text-5xl font-light text-gray-900">
              What makes us different
            </p>
          </div>

          <div className="grid md:grid-cols-1 gap-12 max-w-4xl">
            {principles.map((principle, index) => (
              <div key={index} className="flex gap-8">
                <div className="flex-shrink-0">
                  <span className="text-6xl font-light text-primary/20">
                    {principle.number}
                  </span>
                </div>
                <div>
                  <h3 className="text-2xl font-medium text-gray-900 mb-4">
                    {principle.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    {principle.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="mb-16">
            <h2 className="text-sm uppercase tracking-widest text-primary mb-4">
              How We Operate
            </h2>
            <p className="text-4xl md:text-5xl font-light text-gray-900">
              Built for sustainability and scale
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <div className="mb-8">
                <Calendar className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-2xl font-light mb-4">Year-round support</h3>
                <p className="text-gray-600 leading-relaxed">
                  Preparation for professional school is a marathon, not a
                  sprint. Our mentors commit to supporting their mentees
                  throughout the entire application cycle.
                </p>
              </div>

              {/* <div>
                <Award className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-2xl font-light mb-4">
                  Rigorous mentor selection
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Every mentor goes through a comprehensive application process.
                  We look for not just academic achievement, but genuine
                  commitment to equity and excellent communication skills. Only
                  30% of applicants become mentors.
                </p>
              </div> */}
            </div>

            <div>
              <div className="mb-8">
                <Users className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-2xl font-light mb-4">Smart matching</h3>
                <p className="text-gray-600 leading-relaxed">
                  We pair mentees with mentors based on multiple factors such as
                  field of interest, background, and specific needs of mentees.
                  This personalized approach ensures meaningful connections.
                </p>
              </div>

              {/* <div>
                <Globe className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-2xl font-light mb-4">
                  National reach, local touch
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  While we operate nationally, we understand that each region
                  has unique challenges. Our mentors provide insights specific
                  to state schools, regional opportunities, and local resources.
                </p>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* Join Us CTA */}
      {/* <section className="py-24 bg-primary/5">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
            Be part of the change
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Whether you're a student needing guidance or a professional ready to
            give back, there's a place for you in our community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              variant="outline"
              className="border-primary text-primary hover:bg-primary/5 px-8 py-6 text-base font-normal"
              asChild
            >
              <Link href="/how-it-works">Learn How It Works</Link>
            </Button>
          </div>
        </div>
      </section> */}
    </StaticPageLayout>
  );
}
